import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { ManuscriptPolishReport } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { formatNumber } from "../lib/format.js";

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
}

/**
 * Manuscript Polish: a book-wide Prose Lens report. Ranks scenes by how much
 * editing attention they need and shows words overused across the whole book.
 * Deterministic + AI-free — works with no provider configured.
 */
export function PolishCard({ projectId, onOpenScene }: Props) {
  const toast = useToast();
  const [report, setReport] = useState<ManuscriptPolishReport | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await api.manuscriptPolish(projectId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't analyze the manuscript");
    } finally {
      setLoading(false);
    }
  }, [projectId, toast]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;
  }
  if (!report) return null;

  if (report.totals.analyzedScenes === 0) {
    return (
      <div className="insights-grid insights-grid-single">
        <section className="card insight-card">
          <div className="insight-card-head"><Icon name="sparkles" size={16} style={{ color: "var(--accent)" }} /><h3>Manuscript Polish</h3></div>
          <p className="muted insight-empty">Write a few scenes and this will show where your prose needs the most attention.</p>
        </section>
      </div>
    );
  }

  const { totals, averages, overusedWords, scenes } = report;
  const maxScore = Math.max(1, ...scenes.map((s) => s.attentionScore));

  return (
    <div className="insights-grid insights-grid-single">
      <section className="card insight-card">
        <div className="insight-card-head">
          <Icon name="sparkles" size={16} style={{ color: "var(--accent)" }} />
          <h3>Manuscript Polish</h3>
          <span className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={() => void load()}><Icon name="history" size={13} /> Refresh</button>
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 12px" }}>
          A book-wide read of your prose — scenes ranked by how much editing attention they need. Deterministic, no AI.
        </p>
        <div className="polish-metrics">
          <Metric label="Scenes analyzed" value={`${formatNumber(totals.analyzedScenes)} / ${formatNumber(totals.scenes)}`} />
          <Metric label="Readability" value={averages.readabilityLabel} hint={`grade ~${averages.readabilityGrade}`} />
          <Metric label="Filter words" value={`${averages.filterPerK}/1k`} />
          <Metric label="Adverbs" value={`${averages.adverbPerK}/1k`} />
        </div>
      </section>

      {overusedWords.length > 0 && (
        <section className="card insight-card">
          <div className="insight-card-head"><Icon name="quote" size={16} style={{ color: "var(--accent)" }} /><h3>Overused across the book</h3></div>
          <p className="faint" style={{ fontSize: 12, margin: "0 0 10px" }}>
            Content words you lean on most (character and place names excluded). A few repeats are fine — spikes are worth a look.
          </p>
          <div className="polish-words">
            {overusedWords.map((w) => (
              <span key={w.word} className="polish-word" title={`${w.perTenK} per 10,000 words`}>
                {w.word}<span className="polish-word-n">{formatNumber(w.count)}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="card insight-card">
        <div className="insight-card-head"><Icon name="layers" size={16} style={{ color: "var(--accent)" }} /><h3>Scenes that need attention</h3></div>
        <ul className="polish-scenes">
          {scenes.map((s) => (
            <li key={s.sceneId} className="polish-scene">
              <button className="polish-scene-main" onClick={() => onOpenScene(s.sceneId)} title="Open scene">
                <div className="polish-scene-title">
                  <span className="faint" style={{ fontSize: 11 }}>{s.chapterTitle} · </span>
                  <span className="strong">{s.sceneTitle}</span>
                </div>
                <div className="polish-scene-meta faint">
                  {formatNumber(s.words)} words · {s.readabilityLabel} · {s.avgSentenceLength} w/sentence
                  {s.topIssue && <> · <span className="polish-issue">{s.topIssue}</span></>}
                </div>
              </button>
              <div className="polish-scene-score" title={`Attention score ${s.attentionScore}`}>
                <div className="polish-score-track">
                  <div
                    className={`polish-score-fill ${s.attentionScore >= maxScore * 0.66 ? "high" : s.attentionScore >= maxScore * 0.33 ? "mid" : "low"}`}
                    style={{ width: `${Math.round((s.attentionScore / maxScore) * 100)}%` }}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="polish-metric">
      <div className="polish-metric-value">{value}</div>
      <div className="polish-metric-label">{label}{hint && <span className="faint"> · {hint}</span>}</div>
    </div>
  );
}
